import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import React, { ChangeEventHandler, ComponentProps, ReactNode, Ref, useState } from 'react';

import { CaseManifestFileMenu } from './CaseManifestFileActions';
import { CaseManifestTargetCaseCell, ManifestTargetCaseOption } from './CaseManifestTargetCaseCell';
import { SlideAdditionChoice, SlideAdditionOptions } from './dialogState';

interface CaseManifestSlideAdditionDialogProps {
  caseOptions: ManifestTargetCaseOption[];
  isCasesError: boolean;
  isCasesLoading: boolean;
  open: boolean;
  selectedSlideCount: number;
  onClose: () => void;
  onConfirm: (choice: SlideAdditionChoice, options: SlideAdditionOptions) => void;
}

export const CaseManifestSlideAdditionDialog = ({
  caseOptions,
  isCasesError,
  isCasesLoading,
  open,
  selectedSlideCount,
  onClose,
  onConfirm,
}: CaseManifestSlideAdditionDialogProps) => {
  const [selectedChoice, setChoice] = useState<SlideAdditionChoice['kind']>('individualCases');
  const [existingCaseId, setExistingCaseId] = useState<number | null>(null);
  const [continueAdding, setContinueAdding] = useState(false);
  const hasMultipleSlides = selectedSlideCount > 1;
  const hasExistingCaseOptions = caseOptions.length > 0;
  const hasNoSavedCases = !isCasesLoading && !isCasesError && !hasExistingCaseOptions;
  // The saved-case radio can be picked while cases load and then vanish; fall back to a choice that is still offered.
  const choice =
    (!hasMultipleSlides && (selectedChoice === 'singleCase' || selectedChoice === 'assignManually')) ||
    (hasNoSavedCases && selectedChoice === 'existingCase')
      ? 'individualCases'
      : selectedChoice;
  const existingCaseUnavailable = !isCasesLoading && isCasesError;
  const isSingleSlideWithoutSavedCases = selectedSlideCount === 1 && hasNoSavedCases;
  const addDisabled =
    selectedSlideCount === 0 ||
    (choice === 'existingCase' && (isCasesLoading || isCasesError || existingCaseId === null));

  const resetAndClose = () => {
    setChoice('individualCases');
    setExistingCaseId(null);
    setContinueAdding(false);
    onClose();
  };

  const addSelectedSlides = (shouldContinueAdding = continueAdding) => {
    if (addDisabled) return;
    onConfirm(
      choice === 'existingCase' && existingCaseId !== null
        ? { kind: 'existingCase', caseId: existingCaseId }
        : { kind: choice as 'individualCases' | 'singleCase' | 'assignManually' },
      { continueAdding: shouldContinueAdding }
    );
    resetAndClose();
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="case-manifest-assign-slides-title"
    >
      <DialogChromeHeader
        title="Assign selected slides"
        subtitle="Choose how these slides should be added to the case assignment."
        titleId="case-manifest-assign-slides-title"
        closeButtonAriaLabel="Close add slides dialog"
        onClose={resetAndClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">{selectedSlideCount} selected</Typography>
          {isSingleSlideWithoutSavedCases ? null : (
            <>
              <FormControl>
                <RadioGroup
                  aria-label="How to assign selected slides"
                  value={choice}
                  onChange={(event) => setChoice(event.target.value as SlideAdditionChoice['kind'])}
                >
                  <FormControlLabel
                    value="individualCases"
                    control={<Radio />}
                    label={hasMultipleSlides ? 'Create a new case for each slide' : 'Create a new case'}
                  />
                  {hasMultipleSlides ? (
                    <>
                      <FormControlLabel
                        value="singleCase"
                        control={<Radio />}
                        label="Group all slides into one new case"
                      />
                      <FormControlLabel
                        value="assignManually"
                        control={<Radio />}
                        label="Let me assign each slide myself"
                      />
                    </>
                  ) : null}
                  {hasNoSavedCases ? null : (
                    <FormControlLabel
                      value="existingCase"
                      control={<Radio />}
                      disabled={existingCaseUnavailable}
                      label="Add slides to an existing case"
                    />
                  )}
                </RadioGroup>
                {isCasesLoading ? <FormHelperText>Loading saved cases…</FormHelperText> : null}
                {isCasesError ? (
                  <Alert severity="error">Could not load saved cases. Close this dialog and try again.</Alert>
                ) : null}
              </FormControl>
              {choice === 'existingCase' && hasExistingCaseOptions ? (
                <CaseManifestTargetCaseCell
                  disabled={isCasesLoading || isCasesError}
                  loading={isCasesLoading}
                  onChange={(target) => setExistingCaseId(typeof target === 'number' ? target : null)}
                  options={caseOptions}
                  slideLabel="selected slides"
                  target={existingCaseId}
                />
              ) : null}
              <FormControlLabel
                control={
                  <Checkbox checked={continueAdding} onChange={(event) => setContinueAdding(event.target.checked)} />
                }
                label="Continue adding slides"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogChromeFooter>
        {isSingleSlideWithoutSavedCases ? (
          <>
            <Button variant="outlined" color="inherit" disabled={addDisabled} onClick={() => addSelectedSlides(true)}>
              Assign more slides
            </Button>
            <Button variant="outlined" color="inherit" disabled={addDisabled} onClick={() => addSelectedSlides(false)}>
              Proceed to case assignment
            </Button>
          </>
        ) : (
          <>
            <Button color="inherit" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" disabled={addDisabled} onClick={() => addSelectedSlides()}>
              Add slides
            </Button>
          </>
        )}
      </DialogChromeFooter>
    </Dialog>
  );
};

interface CaseManifestPlanToolbarProps {
  fileMenuProps: ComponentProps<typeof CaseManifestFileMenu>;
  manifestInputRef: Ref<HTMLInputElement>;
  onFileChange: ChangeEventHandler<HTMLInputElement>;
  sourceMenu?: ReactNode;
  title: string;
  uploadDisabled: boolean;
}

export const CaseManifestPlanToolbar = ({
  fileMenuProps,
  manifestInputRef,
  onFileChange,
  sourceMenu,
  title,
  uploadDisabled,
}: CaseManifestPlanToolbarProps) => (
  <Grid container justifyContent="space-between" alignItems="center" mb={2} rowGap={1}>
    <Grid item>
      <Typography variant="h2" sx={{ fontSize: 24 }}>
        {title}
      </Typography>
    </Grid>
    <Grid item>
      <Stack direction="row" spacing={1} alignItems="center">
        {sourceMenu}
        <CaseManifestFileMenu {...fileMenuProps} />
      </Stack>
      <input
        ref={manifestInputRef}
        hidden
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={onFileChange}
        disabled={uploadDisabled}
        id="case-manifest-upload-input"
      />
    </Grid>
  </Grid>
);

interface CaseManifestDialogFooterProps {
  assignmentIssues: string[];
  caseListLoadingIcon: ReactNode;
  editingPaused: boolean;
  hasResettableManifestChanges: boolean;
  hasUploadedManifest: boolean;
  isCasesLoading: boolean;
  isChoosingEntryIntent: boolean;
  isOrganizationView: boolean;
  isSelectingSlides: boolean;
  onClose: () => void;
  onLeaveSelectedSlidesOut?: () => void;
  onNameFromFileName?: () => void;
  onOpenOrganization: () => void;
  onResetChanges: () => void;
  onCreateCasesFromFileNames?: () => void;
  onReviewChanges: () => void;
  onSelectSlidesForCasePlan: () => void;
  footerGuidance: string | null;
  runDisabledReason: string | null;
  selectedSlideCount: number;
  selectionActionDisabled: boolean;
  showSubmit: boolean;
  submissionLabel: string;
}

export const CaseManifestDialogFooter = ({
  assignmentIssues,
  caseListLoadingIcon,
  editingPaused,
  footerGuidance,
  hasResettableManifestChanges,
  hasUploadedManifest,
  isCasesLoading,
  isChoosingEntryIntent,
  isOrganizationView,
  isSelectingSlides,
  onClose,
  onCreateCasesFromFileNames,
  onLeaveSelectedSlidesOut,
  onNameFromFileName,
  onOpenOrganization,
  onResetChanges,
  onReviewChanges,
  onSelectSlidesForCasePlan,
  runDisabledReason,
  selectedSlideCount,
  selectionActionDisabled,
  showSubmit,
  submissionLabel,
}: CaseManifestDialogFooterProps) => (
  <DialogChromeFooter sticky sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1.5 }}>
    {!isChoosingEntryIntent && isOrganizationView && assignmentIssues.length > 0 && (
      <Alert
        severity="warning"
        action={
          onNameFromFileName ? (
            <Button color="inherit" size="small" onClick={onNameFromFileName}>
              Name from file name
            </Button>
          ) : undefined
        }
      >
        {assignmentIssues.slice(0, 3).map((issue) => (
          <Typography key={issue} variant="body2">
            {issue}
          </Typography>
        ))}
        {assignmentIssues.length > 3 ? (
          <Typography variant="body2">{assignmentIssues.length - 3} more assignment issues.</Typography>
        ) : null}
      </Alert>
    )}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
      {!isChoosingEntryIntent && isOrganizationView && (runDisabledReason || footerGuidance) && (
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          {runDisabledReason || footerGuidance}
        </Typography>
      )}
      {!isChoosingEntryIntent && isOrganizationView && onCreateCasesFromFileNames && (
        <Button variant="contained" color="primary" onClick={onCreateCasesFromFileNames} disabled={editingPaused}>
          Create a case from each file name
        </Button>
      )}
      {!isChoosingEntryIntent && isOrganizationView && selectedSlideCount > 0 && onLeaveSelectedSlidesOut && (
        <Button color="inherit" onClick={onLeaveSelectedSlidesOut} disabled={editingPaused}>
          Leave out of assignment
        </Button>
      )}
      {!isChoosingEntryIntent && isOrganizationView && hasUploadedManifest && (
        <Button color="inherit" onClick={onResetChanges} disabled={editingPaused || !hasResettableManifestChanges}>
          Reset changes
        </Button>
      )}
      {isChoosingEntryIntent ? (
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
      ) : isSelectingSlides ? (
        <>
          <Button variant="text" color="inherit" onClick={onOpenOrganization}>
            Return to case assignment
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSelectSlidesForCasePlan}
            disabled={selectionActionDisabled}
          >
            Assign slides to study
          </Button>
        </>
      ) : !isOrganizationView ? (
        <Button variant="contained" color="secondary" onClick={onOpenOrganization}>
          Return to case assignment
        </Button>
      ) : (
        showSubmit && (
          <Button
            variant={onCreateCasesFromFileNames ? 'outlined' : 'contained'}
            color={onCreateCasesFromFileNames ? 'inherit' : 'primary'}
            aria-busy={isCasesLoading}
            startIcon={caseListLoadingIcon}
            onClick={onReviewChanges}
            disabled={Boolean(runDisabledReason) || assignmentIssues.length > 0}
          >
            {submissionLabel}
          </Button>
        )
      )}
    </Box>
  </DialogChromeFooter>
);
